import { useState, useEffect, useRef, KeyboardEvent, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Group, GroupMessage, GroupMember } from '../services/api'
import { useGroupSignalProtocol } from '../hooks/useGroupSignalProtocol'

// Sender name colors (Telegram-style)
const SENDER_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#82E0AA', '#F8C471', '#85C1E9',
]

function getSenderColor(senderId: string): string {
  let hash = 0
  for (let i = 0; i < senderId.length; i++) {
    hash = ((hash << 5) - hash) + senderId.charCodeAt(i)
    hash |= 0
  }
  return SENDER_COLORS[Math.abs(hash) % SENDER_COLORS.length]
}

export default function GroupChatPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { encryptGroupMessage, decryptGroupMessage } = useGroupSignalProtocol()

  const [group, setGroup] = useState<Group | null>(null)
  const [members, setMembers] = useState<GroupMember[]>([])
  const [messages, setMessages] = useState<GroupMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [showMenu, setShowMenu] = useState(false)

  // @mention
  const [showMentions, setShowMentions] = useState(false)
  const [_mentionQuery, setMentionQuery] = useState('')
  const [filteredMembers, setFilteredMembers] = useState<GroupMember[]>([])

  // Long press / message actions
  const [selectedMessage, setSelectedMessage] = useState<GroupMessage | null>(null)
  const longPressTimer = useRef<NodeJS.Timeout | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (groupId && user?.id) loadGroupAndMessages()
  }, [groupId, user?.id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const processMessages = async (msgs: GroupMessage[]) => {
    if (!user?.id) return msgs
    return await Promise.all(msgs.map(async (msg) => {
      let decryptedText = msg.text
      if (msg.ciphertext) {
        const decrypted = await decryptGroupMessage(msg.ciphertext, user.id)
        decryptedText = decrypted
      }
      return { ...msg, text: decryptedText }
    }))
  }

  useEffect(() => {
    if (!groupId || !user?.id) return
    const pollInterval = setInterval(async () => {
      try {
        const newMessages = await api.getGroupMessages(groupId)
        if (newMessages.length > messages.length) {
          const processed = await processMessages(newMessages)
          setMessages(processed)
        }
      } catch (error) {
        console.error('Failed to poll messages:', error)
      }
    }, 3000)
    return () => clearInterval(pollInterval)
  }, [groupId, messages.length, user?.id, decryptGroupMessage])

  const loadGroupAndMessages = async () => {
    try {
      setIsLoading(true)
      const [groupData, messagesData, membersData] = await Promise.all([
        api.getGroup(groupId!),
        api.getGroupMessages(groupId!),
        api.getGroupMembers(groupId!),
      ])
      setGroup(groupData)
      setMembers(membersData)
      const processed = await processMessages(messagesData)
      setMessages(processed)
    } catch (error) {
      console.error('Failed to load group:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessage = async () => {
    const text = newMessage.trim()
    if (!text || !groupId || isSending || !user?.id) return

    setIsSending(true)
    setNewMessage('')
    setShowMentions(false)

    const optimisticMessage: GroupMessage = {
      id: `temp-${Date.now()}`,
      senderId: user.id,
      senderName: user.username || '',
      ciphertext: '',
      timestamp: new Date().toISOString(),
      text: text,
    }

    setMessages(prev => [...prev, optimisticMessage])
    inputRef.current?.focus()

    try {
      const memberIds = members.map(m => m.id)
      const ciphertext = await encryptGroupMessage(text, memberIds, user.id)
      const sentMessage = await api.sendGroupMessage(groupId, ciphertext)
      setMessages(prev =>
        prev.map(msg =>
          msg.id === optimisticMessage.id ? { ...sentMessage, text } : msg
        )
      )
    } catch (error) {
      console.error('Failed to send message:', error)
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id))
      alert('發送失敗: ' + (error as Error).message)
    } finally {
      setIsSending(false)
    }
  }

  const handleInputChange = (value: string) => {
    setNewMessage(value)

    // Detect @mention
    const cursorPos = inputRef.current?.selectionStart || value.length
    const textBeforeCursor = value.slice(0, cursorPos)
    const atMatch = textBeforeCursor.match(/@(\w*)$/)

    if (atMatch) {
      const query = atMatch[1].toLowerCase()
      setMentionQuery(query)
      setShowMentions(true)
      setFilteredMembers(
        members.filter(m =>
          m.id !== user?.id &&
          m.username.toLowerCase().includes(query)
        ).slice(0, 5)
      )
    } else {
      setShowMentions(false)
    }
  }

  const handleSelectMention = (member: GroupMember) => {
    const cursorPos = inputRef.current?.selectionStart || newMessage.length
    const textBeforeCursor = newMessage.slice(0, cursorPos)
    const textAfterCursor = newMessage.slice(cursorPos)
    const replaced = textBeforeCursor.replace(/@\w*$/, `@${member.username} `)
    setNewMessage(replaced + textAfterCursor)
    setShowMentions(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Long press handlers
  const handleMessageTouchStart = useCallback((msg: GroupMessage) => {
    longPressTimer.current = setTimeout(() => {
      setSelectedMessage(msg)
    }, 500)
  }, [])

  const handleMessageTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const handlePinMessage = async () => {
    if (!selectedMessage || !groupId) return
    try {
      const result = await api.pinGroupMessage(groupId, selectedMessage.id)
      alert(result.action === 'pinned' ? '已釘選' : '已取消釘選')
    } catch (error) {
      console.error('Failed to pin:', error)
      alert('操作失敗')
    }
    setSelectedMessage(null)
  }

  const handleLeaveGroup = async () => {
    if (!groupId) return
    if (!confirm('確定要離開群組嗎？')) return
    try {
      await api.leaveGroup(groupId)
      navigate('/chats')
    } catch (error) {
      console.error('Failed to leave group:', error)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const isAdmin = group?.my_role === 'admin' || group?.my_role === 'owner'

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="spinner border-primary border-t-primary-light" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar sticky top-0 z-10">
        <button
          onClick={() => navigate('/chats')}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Clickable Group Header → Info Page */}
        <button
          onClick={() => navigate(`/group/${groupId}/info`)}
          className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity"
        >
          <div className="w-9 h-9 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
            {group?.avatar_url ? (
              <img src={group.avatar_url} alt={group?.name} className="w-full h-full object-cover rounded-full" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="font-semibold truncate text-left">{group?.name || '群組'}</h1>
            <p className="text-xs text-white/70 text-left">{group?.member_count || members.length} 位成員</p>
          </div>
        </button>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
              <button
                onClick={() => { setShowMenu(false); navigate(`/group/${groupId}/info`) }}
                className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                群組資訊
              </button>
              <button
                onClick={() => { setShowMenu(false); navigate(`/group/${groupId}/members`) }}
                className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                成員列表
              </button>
              <button
                onClick={() => { setShowMenu(false); handleLeaveGroup() }}
                className="w-full px-4 py-3 text-left text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                離開群組
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-3 py-2">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400">開始在群組中聊天吧！</p>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((message, idx) => {
              const isSent = message.senderId === user?.id
              const prevMsg = idx > 0 ? messages[idx - 1] : null
              const showSender = !isSent && (!prevMsg || prevMsg.senderId !== message.senderId)
              const senderColor = getSenderColor(message.senderId)

              return (
                <div
                  key={message.id}
                  className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                  onTouchStart={() => handleMessageTouchStart(message)}
                  onTouchEnd={handleMessageTouchEnd}
                  onContextMenu={(e) => { e.preventDefault(); setSelectedMessage(message) }}
                >
                  <div className={`max-w-[80%] ${isSent ? 'items-end' : 'items-start'}`}>
                    {/* Sender name */}
                    {showSender && message.senderName && (
                      <p className="text-xs font-medium ml-2 mb-0.5" style={{ color: senderColor }}>
                        {message.senderName}
                      </p>
                    )}
                    {/* Bubble */}
                    <div className={`rounded-2xl px-3 py-2 shadow-sm ${
                      isSent
                        ? 'bg-primary text-white rounded-br-md'
                        : 'bg-white dark:bg-dark-surface text-gray-900 dark:text-white rounded-bl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{message.text || message.ciphertext}</p>
                      <p className={`text-[10px] mt-0.5 text-right ${
                        isSent ? 'text-white/60' : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* @Mention Autocomplete */}
      {showMentions && filteredMembers.length > 0 && (
        <div className="bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-gray-800 shadow-lg max-h-40 overflow-y-auto">
          {filteredMembers.map(member => (
            <button
              key={member.id}
              onClick={() => handleSelectMention(member)}
              className="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                {member.avatar_url ? (
                  <img src={member.avatar_url} alt="" className="w-full h-full object-cover rounded-full" />
                ) : (
                  member.username.charAt(0).toUpperCase()
                )}
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">@{member.username}</span>
            </button>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-gray-800 px-4 py-3 sticky bottom-0">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="輸入訊息..."
              rows={1}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-3xl resize-none
                         focus:outline-none focus:ring-2 focus:ring-primary/50
                         dark:text-white placeholder:text-gray-400"
              style={{ maxHeight: '120px' }}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending}
            className="p-3 bg-primary text-white rounded-full
                       hover:bg-primary-dark transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Message Action Sheet */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setSelectedMessage(null)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
            {/* Preview */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-xs text-gray-500 mb-0.5">{selectedMessage.senderName || selectedMessage.senderId}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                {selectedMessage.text || selectedMessage.ciphertext}
              </p>
            </div>

            {/* Copy */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(selectedMessage.text || selectedMessage.ciphertext || '')
                setSelectedMessage(null)
              }}
              className="w-full px-4 py-3.5 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              複製
            </button>

            {/* Pin (admin only) */}
            {isAdmin && (
              <button
                onClick={handlePinMessage}
                className="w-full px-4 py-3.5 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                釘選訊息
              </button>
            )}

            {/* Reply (insert @mention) */}
            <button
              onClick={() => {
                const name = selectedMessage.senderName || ''
                if (name && selectedMessage.senderId !== user?.id) {
                  setNewMessage(prev => `@${name} ${prev}`)
                }
                setSelectedMessage(null)
                inputRef.current?.focus()
              }}
              className="w-full px-4 py-3.5 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              回覆
            </button>

            {/* Cancel */}
            <button
              onClick={() => setSelectedMessage(null)}
              className="w-full px-4 py-3.5 text-center text-gray-500 font-medium border-t border-gray-100 dark:border-gray-800"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {showMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
      )}
    </div>
  )
}
